import React from 'react';

import { Badge, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';

import { anchovy, coolGrey, squidInk } from 'lib/css/colors';
import Container from 'components/shared/Container';
import { getJobRunStatusVariant } from 'lib/enums/JobRunStatus';
import { JOB_RUNS_BY_TARGET } from 'lib/networking/endpoints';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import useGet from 'lib/hooks/useGet';
import UserPermission from 'lib/enums/UserPermission';
import useUserPermissions from 'lib/hooks/useUserPermissions';

const CLEANUP_JOB_CLASS_NAME = 'ProcessFlaggedBrandOutlierProducts';
const CLEANUP_JOB_METHOD = 'process_flagged_brand_outlier_products';
const RUNS_LIMIT = 10;

const EMPTY_VALUE = '-';

const formatCount = value => (value == null ? EMPTY_VALUE : Number(value).toLocaleString('en-US'));

const formatRunDate = value => {
  if (!value) {
    return EMPTY_VALUE;
  }
  return new Date(value).toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  });
};

const formatOutlierRate = (outlierCount, totalCount) => {
  if (outlierCount == null || !totalCount) {
    return null;
  }
  return `${((outlierCount / totalCount) * 100).toFixed(1)}%`;
};

function RecurringJobsSection() {
  const hasUserPermissions = useUserPermissions();
  const canViewJobs = hasUserPermissions([UserPermission.VIEW_JOBS]);

  const { data: { data: runs = [] } = {}, loading } = useGet(
    JOB_RUNS_BY_TARGET,
    {
      class_name: CLEANUP_JOB_CLASS_NAME,
      method: CLEANUP_JOB_METHOD,
      limit: RUNS_LIMIT,
    },
    !canViewJobs,
  );

  if (!canViewJobs) {
    return null;
  }

  return (
    <Container className={css(styles.section)}>
      <h2 className={css(styles.title)}>Recurring Jobs</h2>
      <div className={css(styles.subsectionHeader)}>
        <h3 className={css(styles.subtitle)}>Cleanup Runs</h3>
        <span className={css(styles.caption)}>Product outlier detection · most recent runs</span>
      </div>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Table className={css(styles.tableText)}>
          <thead className={css(styles.thead)}>
            <tr>
              <th>Run</th>
              <th>Status</th>
              <th className={css(styles.numericColumn)}>Products Processed</th>
              <th className={css(styles.numericColumn)}>Outliers Identified</th>
              <th className={css(styles.numericColumn)}>Brands Cleaned</th>
            </tr>
          </thead>
          <tbody>
            {runs.length ? (
              runs.map(run => {
                const metadata = run.result_metadata || {};
                const outlierRate = formatOutlierRate(
                  metadata.total_outlier_products_processed,
                  metadata.total_products_processed,
                );
                return (
                  <tr key={run.id}>
                    <td>
                      {/* PENDING runs have no started_at, so fall back to when it was queued */}
                      <div>{formatRunDate(run.started_at || run.date_created)}</div>
                      <small className={css(styles.runSubText)}>#{run.id}</small>
                    </td>
                    <td>
                      <Badge variant={getJobRunStatusVariant(run.status)}>{run.status}</Badge>
                    </td>
                    <td className={css(styles.numericColumn)}>
                      {formatCount(metadata.total_products_processed)}
                    </td>
                    <td className={css(styles.numericColumn)}>
                      {formatCount(metadata.total_outlier_products_processed)}
                      {outlierRate && <span className={css(styles.rateTag)}>{outlierRate}</span>}
                    </td>
                    <td className={css(styles.numericColumn)}>
                      {formatCount(metadata.total_brands_marked_as_cleaned)}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={5} className={css(styles.emptyRow)}>
                  No cleanup runs yet
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
    </Container>
  );
}

const styles = StyleSheet.create({
  section: {
    padding: '20px',
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    color: squidInk,
    marginBottom: 16,
  },
  subsectionHeader: {
    alignItems: 'baseline',
    display: 'flex',
    gap: 8,
    marginBottom: 12,
  },
  subtitle: {
    fontSize: 15,
    fontWeight: 600,
    color: squidInk,
    marginBottom: 0,
  },
  caption: {
    fontSize: 12,
    color: anchovy,
  },
  tableText: {
    fontSize: 14,
  },
  thead: {
    color: coolGrey,
  },
  numericColumn: {
    textAlign: 'right',
  },
  runSubText: {
    color: anchovy,
  },
  rateTag: {
    color: anchovy,
    marginLeft: 6,
  },
  emptyRow: {
    color: anchovy,
    textAlign: 'center',
  },
});

export default RecurringJobsSection;
